import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { API_URLS } from "../config/api";
import { 
  initiatePayment, 
  validatePaymentPrerequisites
  
} from "../utils/paymentHelpersNew";

// -- helper: File -> base64
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// -- helper: selected form data ko safe, compact structure me store karna
const buildPhotoBase64 = async (data) => {
  const {
    photo
  } = data;

  let photoBase64 = null;
  if (photo && photo[0]) {
    photoBase64 = await fileToBase64(photo[0]);
  }

  return {
    photoBase64
  };
};

const RegistrationFormNew = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalFee, setTotalFee] = useState(0);
  const [fees, setFees] = useState(null);
  const [feesError, setFeesError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [ageValidation, setAgeValidation] = useState({
    isValid: true,
    message: ''
  });

  // Array of images to rotate
  const images = [
    '/images/20260214_League.jpeg',
    '/images/cricket-equipment-digital-art_23-2151761259.jpg',
    '/images/s2.jpg'    
  ];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();

  // Calculate registration fee based on player type using backend fees
  const calculateRegistrationFee = (playerType) => {
    // If fees are not loaded, return null
    if (!fees) {
      return null;
    }
    
    // Return 0 if no player type is selected
    if (!playerType || playerType.trim() === '') {
      return 0;
    }
    
    const t = playerType.toLowerCase();
    if (t.includes('old')) return fees.old;
    if (t.includes('child')) return fees.child;
    if (t.includes('flat')) return fees.flatFee;
    return fees.new;
  };

  // Calculate registration fee based on player type
  useEffect(() => {
    const playerType = watch("playerType");
    const fee = calculateRegistrationFee(playerType);
    setTotalFee(fee !== null ? fee : 0);
  }, [watch("playerType"), fees]);

  // Fetch events on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // console.log('🔍 Fetching upcoming events for registration from:', API_URLS.UPCOMING_FOR_REGISTRATION);
        const eventsResponse = await axios.get(API_URLS.UPCOMING_FOR_REGISTRATION);
        
        // Ensure events is always an array
        if (Array.isArray(eventsResponse.data)) {
          setEvents(eventsResponse.data);
        } else if (eventsResponse.data && Array.isArray(eventsResponse.data.events)) {
          setEvents(eventsResponse.data.events);
        } else {
          setEvents([]);
        }
      } catch (error) {
        // console.error('❌ Failed to fetch events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  //Remove after Flat Fee Working 
  const selectRef = useRef();

  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    }
  }, []);
  //end Region
  // Fetch fees from backend on component mount
  useEffect(() => {
    const fetchFees = async () => {
      try {
        const response = await axios.get(API_URLS.PAYMENT_FEES);
        if (response.data.success && response.data.fees) {
          //console.warn(response.data.fees);
          setFees(response.data.fees);
          setFeesError(false);
        } else {
          setFees(null);
          setFeesError(true);
        }
      } catch (error) {
        setFees(null);
        setFeesError(true);
      }
    };
    
    fetchFees();
  }, []);

  // Auto-rotate images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [images.length]);


  // Handle player type change
  const handlePlayerTypeChange = (e) => {
    const newPlayerType = e.target.value;
    
    // Immediately update form state and calculate fee
    setValue("playerType", newPlayerType);
    const fee = calculateRegistrationFee(newPlayerType);
    setTotalFee(fee);
    
    // Clear age validation when switching player types
    setAgeValidation({
      isValid: true,
      message: ''
    });
  };

  // Handle age validation for all player types
  const handleAgeChange = (e) => {
    const age = parseInt(e.target.value);
    const playerType = watch('playerType');
    
    if (playerType === 'Child Player (Kabir Nagar)') {
      // Child Player: 6 < age <= 16
      if (age <= 6 || age > 16) {
        setAgeValidation({
          isValid: false,
          message: 'Child Player age must be between 7-16 years'
        });
      } else {
        setAgeValidation({
          isValid: true,
          message: ''
        });
      }
    } else {
      // Other Players: 6 < age < 55
      if (age <= 6 || age >= 55) {
        setAgeValidation({
          isValid: false,
          message: 'Age must be between 7-54 years'
        });
      } else {
        setAgeValidation({
          isValid: true,
          message: ''
        });
      }
    }
  };

  // Handle declaration agreement
  const handleDeclarationAgree = () => {
    setValue('declaration', true);
    setShowDeclarationModal(false);
  };

  // Handle declaration disagreement
  const handleDeclarationDisagree = () => {
    setValue('declaration', false);
    setShowDeclarationModal(false);
  };

  const onSubmit = async (data) => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Check declaration agreement
    if (!data.declaration) {
      alert('You must agree to the terms and conditions to proceed');
      return;
    }

    // ✅ SIMPLIFIED: Use comprehensive validation function
    const validationErrors = validatePaymentPrerequisites(data);
    if (validationErrors.length > 0) {
      alert(validationErrors[0]);
      return;
    }

    // Additional age validation
    if (data.age <= 6 || data.age >= 55) {
      alert('Age must be between 7-54 years');
      return;
    }

    // Child Player specific age validation
    if (data.playerType === 'Child Player (Kabir Nagar)' && (data.age <= 6 || data.age > 16)) {
      alert('Child Player age must be between 7-16 years');
      return;
    }

    try {
      // Convert photo to base64 for database storage
      const pending = await buildPhotoBase64(data);
      
      // ✅ CHANGE: Include ALL data needed for database storage
      const playerData = {
        name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
        mobile: data.mobile,
        playerType: data.playerType,
        selectedEvent: data.selectedEvent,
        age: data.age,
        playingStyle: data.playingStyle,
        unavailability: data.unavailability,
        address: data.address,
        remarks: data.remarks,
       photoBase64:pending.photoBase64, 
        email: `${data.mobile}@${process.env.REACT_APP_EMAIL_DOMAIN}`,// EMAIL IS NOR PROVIDED IN REGISTRATION FORM
        regFee : totalFee ,        
        cricheroesID: data.cricheroesID,
      };

      // Load payment config
      try {
        const cfgRes = await axios.get('/api/paymentNew/config', { timeout: 10000 });
        window.__NDPS_CFG__ = cfgRes.data?.data || {};
      } catch (e) {
        // console.warn('⚠️ Could not load payment config, using fallback');
      }

      // ✅ CHANGE: Pass complete playerData (backend will save to database)
      await initiatePayment(playerData);
      
    } catch (error) {
      // console.error('❌ Payment initiation error:', error);
      alert('Could not start payment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-2">
      <div className="bg-white shadow-2xl rounded-2xl flex flex-col lg:flex-row w-full max-w-5xl border border-gray-200 backdrop-blur-sm">

        {/* Left side image */}
        <div className="lg:w-2/5 w-full h-[600px]">
          <img
            src={images[currentImageIndex]}
            alt="Cricket Equipment"
            className="object-cover h-full w-full rounded-l-lg"
          />
        </div>

        {/* Right side form */}
        <div className="lg:w-3/5 w-full p-6">
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-gray-800 mb-2">
              Player <span className="text-blue-600 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Registration</span> Form
            </h1>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600 mx-auto rounded-full shadow-lg"></div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">

            {/* Register for Events and Player Type - Side by side */}
            <div className="flex gap-3 mb-2">
              {/* Register for Events */}
              <div className="flex-1">
                {loading ? (
                  <div className="text-center py-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-1">Loading events...</p>
                  </div>
                ) : events.length > 0 ? (
                  <div>
                    <select
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md bg-white/80 backdrop-blur-sm"
                      {...register("selectedEvent", { required: "Please select an event" })}
                    >
                      <option value="">Event to register</option>
                      {Array.isArray(events) && events.map((event) => {
                        // Format dates properly
                        const formatDate = (dateString) => {
                          if (!dateString) return 'No Date';
                          const date = new Date(dateString);
                          return date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          });
                        };
                        
                        const startDate = formatDate(event.startDate);
                        const endDate = formatDate(event.endDate);
                        
                        return (
                          <option key={event._id} value={event._id}>
                            {event.eventName} ({startDate} - {endDate})
                          </option>
                        );
                      })}
                    </select>
                    {errors.selectedEvent && <span className="text-red-500 text-xs mt-1">{errors.selectedEvent.message}</span>}
                  </div>
                ) : (
                  <div className="text-center py-1 border border-gray-300 rounded bg-gray-50">
                    <p className="text-xs text-gray-500">No upcoming events available</p>
                  </div>
                )}
              </div>

              {/* Player Type */}
              <div className="flex-1">
                <select
                  ref={selectRef}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md bg-white/80 backdrop-blur-sm"
                  {...register("playerType", { required: "Player type is required" })}
                  onChange={handlePlayerTypeChange}
                >
                  {/* <option value="Flat Fee">Registration Fee (800)</option> */}
                  
                  <option value="">Player Type</option>
                  <option value="New Player">New Player</option>
                  <option value="Old Player">Old Player</option>
                  {/* <option value="Child Player (Kabir Nagar)">Child Player (Kabir Nagar) - 16 वर्ष तक</option>
                  <option value="Flat Fee">Registration Fee (800)</option> */}
                </select>
                {errors.playerType && <span className="text-red-500 text-xs mt-1">{errors.playerType.message}</span>}
              </div>
            </div>

            {/* Playing Style and Unavailability - Side by side */}
            <div className="flex gap-3 mb-2">
              {/* Playing Style */}
              <div className="flex-1">
                <select
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md bg-white/80 backdrop-blur-sm"
                  {...register("playingStyle", { required: "Playing style is required" })}
                >
                  <option value="">Playing Style</option>
                  <option value="Right Hand Batsman">Right Hand Batsman</option>
                  <option value="Left Hand Batsman">Left Hand Batsman</option>
                  <option value="Right Arm Bowler">Right Arm Bowler</option>
                  <option value="Left Arm Bowler">Left Arm Bowler</option>
                  <option value="Wicket Keeper-Batsman">Right Hand- All rounder </option>
                  <option value="Wicket Keeper-Batsman">Left Hand- All rounder</option>
                  <option value="Wicket Keeper-Batsman">Wicket Keeper</option>
                </select>
                {errors.playingStyle && <span className="text-red-500 text-xs mt-1">{errors.playingStyle.message}</span>}
              </div>

              {/* Unavailability Date */}
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-xs text-gray-500">Unavailability</span>
                    <div className="ml-2 w-px h-4 bg-gray-300"></div>
                  </div>
                  <input
                    type="text"
                    placeholder="Enter dates or periods (Optional)"
                    className="w-full pl-24 pr-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    {...register("unavailability")}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Auto-comma on space
                      if (value.endsWith(' ') && !value.endsWith(', ')) {
                        e.target.value = value.trim() + ', ';
                      }
                    }}
                    onKeyDown={(e) => {
                      // Auto-comma on space key
                      if (e.key === ' ') {
                        e.preventDefault();
                        const value = e.target.value;
                        if (!value.endsWith(', ')) {
                          e.target.value = value + ', ';
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* First Name and Last Name - Side by side */}
            <div className="flex gap-3 mb-2">
              <div className="flex-1">
                <input 
                  type="text" 
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                  placeholder="First name"
                  {...register("firstName", { 
                    required: "First name is required",
                    pattern: {
                      value: /^[a-zA-Z\s]+$/,
                      message: "First name can only contain letters and spaces"
                    }
                  })} 
                />
                {errors.firstName && <span className="text-red-500 text-xs mt-1">{errors.firstName.message}</span>}
              </div>

              <div className="flex-1">
                <input 
                  type="text" 
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                  placeholder="Last name"
                  {...register("lastName", { 
                    required: "Last name is required",
                    pattern: {
                      value: /^[a-zA-Z\s]+$/,
                      message: "Last name can only contain letters and spaces"
                    }
                  })} 
                />
                {errors.lastName && <span className="text-red-500 text-xs mt-1">{errors.lastName.message}</span>}
              </div>
            </div>

            {/* Address */}
            <div className="mb-2">
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                placeholder="Address"
                {...register("address", { required: "Address is required" })}
              />
              {errors.address && <span className="text-red-500 text-xs mt-1">{errors.address.message}</span>}
            </div>

            {/* Age and Mobile Number  */}
            <div className="flex gap-3 mb-2 justify-start">
              <div className="w-20">
                  <input
                    {...register('age', { 
                      required: 'Age is required',
                      min: { value: 7, message: 'Age must be at least 7 years' },
                      max: { value: 54, message: 'Age must be less than 55 years' }
                    })}
                    type="number"
                    placeholder="Age"
                    onChange={handleAgeChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                  />
                  {errors.age && (
                    <p className="text-red-500 text-sm mt-1">{errors.age.message}</p>
                  )}
                  {!ageValidation.isValid && (
                    <p className="text-red-500 text-sm mt-1">{ageValidation.message}</p>
                  )}
                  {watch('playerType') === 'Child Player (Kabir Nagar)' && (
                    <p className="text-blue-500 text-sm mt-1">Child Player age must be between 7-16 years</p>
                  )}
              </div>

              <div className="w-30">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-sm text-gray-500">+91</span>
                    <div className="ml-2 w-px h-4 bg-gray-300"></div>
                  </div>
                  <input
                    type="tel"
                    className="w-full pl-12 pr-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                    placeholder="Mobile Number"
                    maxLength="10"
                    {...register("mobile", { 
                      required: "Mobile number is required",
                      pattern: {
                        value: /^[6-9]\d{9}$/,
                        message: "Please enter a valid Indian mobile number"
                      }
                    })}
                  />
                </div>
                {errors.mobile && <span className="text-red-500 text-xs mt-1">{errors.mobile.message}</span>}
              </div>

              <div className="w-1/2">
                <input
                  type="text"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                  placeholder="Cricheroes ID no"
                  maxLength="20"
                  {...register("cricheroesID")}
                />
                {errors.cricheroesID && <span className="text-red-500 text-xs mt-1">{errors.cricheroesID.message}</span>}
              </div>
            </div>

            {/* Remarks */}
            <div className="mb-2">
              <textarea
                rows="1"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md resize-none"
                placeholder="Remarks (Optional)"
                {...register("remarks")}
              />
            </div>

            {/* Photo Upload */}
            <div className="mb-2">
              <div className="flex flex-row items-stretch gap-3">
                {/* Left Side: Photo Upload */}
                <div className="relative flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    {...register("photo", { required: "Photo is required" })}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Update the display text
                        const label = e.target.nextElementSibling;
                        label.textContent = file.name;
                        // Create preview
                        const previewUrl = URL.createObjectURL(file);
                        setPhotoPreview(previewUrl);
                      }
                    }}
                  />
                  <div className="flex items-center justify-center w-full h-full px-4 py-2 text-sm border-2 border-dashed border-gray-300 rounded-lg shadow-inner hover:border-blue-400 hover:shadow-md transition-all duration-200 bg-gray-50 hover:bg-blue-50 cursor-pointer">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-gray-600 font-medium">Choose Photo</span>
                    </div>
                    
                    {/* Preview Icon */}
                    {photoPreview && (
                      <div 
                        onClick={() => setShowPhotoModal(true)}
                        className="absolute right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-600 transition-all duration-200 z-20"
                      >
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side: Photo Note */}
                <div className="flex items-center bg-yellow-50 border-2 border-yellow-200 rounded-lg px-3 py-2 flex-1">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-yellow-700">
                      <span className="font-semibold">Note:</span> The Photo should not be more than 6 months old
                    </p>
                  </div>
                </div>
              </div>
              {errors.photo && <span className="text-red-500 text-xs mt-1">{errors.photo.message}</span>}
            </div>

            {/* Photo Modal */}
            {showPhotoModal && photoPreview && (
              <div 
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                onClick={() => setShowPhotoModal(false)}
              >
                <div className="relative max-w-2xl max-h-2xl p-4">
                  <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
                    <div className="flex justify-between items-center p-4 border-b">
                      <h3 className="text-lg font-semibold text-gray-900">Photo Preview</h3>
                      <button
                        onClick={() => setShowPhotoModal(false)}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="p-4">
                      <img
                        src={photoPreview}
                        alt="Photo Preview"
                        className="w-full h-auto max-h-96 object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Declaration and Total Fee - Side by side */}
            <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-lg">
              <div className="flex items-center justify-between">
                {/* Left: Declaration Checkbox */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="declaration"
                    checked={watch('declaration') || false}
                    onChange={() => setShowDeclarationModal(true)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="declaration" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-medium text-gray-900">I agree to the terms and conditions</span>
                  </label>
                </div>
                
                {/* Right: Total Fee */}
                <div className="text-right">
                  {feesError || !fees ? (
                    <span className="text-sm font-medium text-red-600">
                      Fee error - Please contact developer
                    </span>
                  ) : totalFee > 0 ? (
                    <>
                      <span className="text-sm font-medium text-gray-700">Registration Fees : </span>
                      <span className="text-lg font-bold text-blue-600">₹{totalFee}</span>
                    </>
                  ) : null}
                </div>
              </div>
              {errors.declaration && (
                <p className="text-red-500 text-xs mt-2">{errors.declaration.message}</p>
              )}
            </div>

            {/* Declaration Modal */}
            {showDeclarationModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Declaration :</h3>
                    <button
                      onClick={() => setShowDeclarationModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="mb-6">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      I, hereby declare that all the information given by me are correct in my knowledge and I hereby giving my concern that committee will not be responsible for any kind of physical damage, accident or other losses during the league. I will be solely responsible for all above things.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowDeclarationModal(false)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    {watch('declaration') ? (
                      <button
                        onClick={handleDeclarationDisagree}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                      >
                        I Disagree
                      </button>
                    ) : (
                      <button
                        onClick={handleDeclarationAgree}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        I Agree
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4 justify-start">
              <div
                onClick={handleSubmit(onSubmit)}
                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 font-medium text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer border active:scale-95 ${
                  isSubmitting || !ageValidation.isValid || !watch('declaration')
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 border-blue-500 hover:border-purple-500'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Register & Pay</span>
                  </>
                )}
              </div>
              
              <div
                onClick={() => {
                  reset();
                  setPhotoPreview(null);
                  // Clear file input
                  const fileInput = document.querySelector('input[type="file"]');
                  if (fileInput) {
                    fileInput.value = '';
                    const label = fileInput.nextElementSibling.querySelector('span');
                    if (label) {
                      label.textContent = 'Choose Photo';
                    }
                  }
                }}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300 font-medium text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer border border-gray-400 hover:border-gray-500 active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFormNew;